import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { hmacSha1 } from '../utils/cryptoUtils';

/**
 * واجهة إعدادات Cloudinary
 */
export interface CloudinarySettings {
  cloudName: string;
  apiKey: string;
  apiSecret: string;
  uploadPreset: string;
  folder: string;
}

// الإعدادات الافتراضية
const DEFAULT_CLOUDINARY_SETTINGS: CloudinarySettings = {
  cloudName: '',
  apiKey: '',
  apiSecret: '',
  uploadPreset: '',
  folder: 'maintenance-system'
};

/**
 * الحصول على إعدادات Cloudinary
 * @returns وعد بإعدادات Cloudinary
 */
export const getCloudinarySettings = async (): Promise<CloudinarySettings> => {
  try {
    // محاولة الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'cloudinary'));

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as CloudinarySettings;
      return {
        cloudName: data.cloudName || '',
        apiKey: data.apiKey || '',
        apiSecret: data.apiSecret || '',
        uploadPreset: data.uploadPreset || '',
        folder: data.folder || 'maintenance-system'
      };
    }

    // إذا لم تكن الإعدادات موجودة، استخدم الإعدادات الافتراضية
    return DEFAULT_CLOUDINARY_SETTINGS;
  } catch (error) {
    console.error('Error getting Cloudinary settings:', error);
    return DEFAULT_CLOUDINARY_SETTINGS;
  }
};

/**
 * حفظ إعدادات Cloudinary
 * @param settings إعدادات Cloudinary
 * @returns وعد بنتيجة عملية الحفظ
 */
export const saveCloudinarySettings = async (settings: CloudinarySettings): Promise<void> => {
  try {
    await setDoc(doc(db, 'settings', 'cloudinary'), {
      ...settings,
      updatedAt: new Date()
    });

    console.log('Cloudinary settings saved successfully');
  } catch (error) {
    console.error('Error saving Cloudinary settings:', error);
    throw error;
  }
};
/**
 * تحميل صورة إلى Cloudinary أو تحويلها إلى Base64 محلياً
 * @param file ملف الصورة المراد تحميله
 * @param folder المجلد (اختياري)
 * @returns وعد بعنوان URL الصورة
 */
export const uploadImage = async (file: File, folder?: string): Promise<string> => {
  try {
    // استخدام خدمة imageStorageService للتحميل حسب الإعدادات المحددة
    const { uploadImage: uploadImageBySettings } = await import('./imageStorageService');
    const result = await uploadImageBySettings(file, folder);
    // استخدام الرابط الرئيسي من نتيجة التحميل
    return result.imageUrl || result.imgbbUrl || result.imagekitUrl || '';
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

/**
 * تحميل مجموعة صور
 * @param files مصفوفة ملفات الصور المراد تحميلها
 * @param folder المجلد
 * @returns وعد بمصفوفة عناوين URL للصور
 */
export const uploadMultipleImages = async (files: File[], folder?: string): Promise<string[]> => {
  try {
    // استخدام خدمة imageStorageService للتحميل المتعدد حسب الإعدادات المحددة
    const { uploadMultipleImages: uploadMultipleImagesBySettings } = await import('./imageStorageService');
    const results = await uploadMultipleImagesBySettings(files, folder);
    
    // تحويل نتائج التحميل إلى مصفوفة من روابط URL
    return results.map(result => 
      result.imageUrl || result.imgbbUrl || result.imagekitUrl || ''
    );
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
};

/**
 * استخراج معرف الصورة من عنوان URL
 * @param url عنوان URL للصورة
 * @returns معرف الصورة
 */
export const getImageIdFromUrl = (url: string): string => {
  // التحقق من أن URL هو URL Cloudinary
  if (url && url.includes('cloudinary.com')) {
    // استخراج معرف الصورة من URL
    const matches = url.match(/\/v\d+\/(.+?)\./);
    if (matches && matches[1]) {
      return matches[1];
    }
  }

  // إذا لم يكن URL Cloudinary، أو لم يتم العثور على معرف
  return 'local-image';
};

/**
 * حذف صورة من Cloudinary
 * @param publicId معرف الصورة
 * @returns وعد بنتيجة عملية الحذف
 */
export const deleteImage = async (publicId: string): Promise<boolean> => {
  try {
    // التحقق من أن معرف الصورة ليس محلياً
    if (publicId === 'local-image' || publicId.startsWith('data:')) {
      console.log('الصورة محلية، لا يمكن حذفها من Cloudinary');
      return true;
    }

    // الحصول على إعدادات Cloudinary
    const settings = await getCloudinarySettings();

    // التحقق من وجود إعدادات Cloudinary
    if (settings.cloudName && settings.apiKey && settings.apiSecret) {
      console.log(`حذف الصورة من Cloudinary: ${publicId}`);

      // إنشاء توقيع للتحقق من الهوية
      const timestamp = Math.floor(Date.now() / 1000);
      const signature = await hmacSha1(`public_id=${publicId}&timestamp=${timestamp}`, settings.apiSecret);

      // إنشاء نموذج FormData
      const formData = new FormData();
      formData.append('public_id', publicId);
      formData.append('api_key', settings.apiKey);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);

      // إرسال الطلب إلى Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${settings.cloudName}/image/destroy`,
        {
          method: 'POST',
          body: formData
        }
      );

      // التحقق من نجاح الطلب
      if (!response.ok) {
        throw new Error(`فشل حذف الصورة: ${response.statusText}`);
      }

      // الحصول على البيانات
      const data = await response.json();

      console.log('نتيجة حذف الصورة:', data);
      return data.result === 'ok';
    } else {
      console.log('إعدادات Cloudinary غير مكتملة، لا يمكن حذف الصورة');
      return false;
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
};

export default {
  getCloudinarySettings,
  saveCloudinarySettings,
  uploadImage,
  uploadMultipleImages,
  getImageIdFromUrl,
  deleteImage
};